import "react-datepicker/dist/react-datepicker.css";
import "./ChooseDateAndTimeStyles.scss";
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Button, Modal } from "react-bootstrap";
import dayjs from "dayjs";
import { Hero } from "components/generic/Hero";
import { CrossIcon, LeftArrowIcon, RightArrowIcon } from "components/icons/Icons";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import { scrollToTop } from "helpers/ScrollingHelper";
import enGB from "date-fns/locale/en-GB";
import da from "date-fns/locale/da";
import DatePicker, { registerLocale } from "react-datepicker";
import advancedFormat from "dayjs/plugin/advancedFormat";
import "dayjs/locale/da";
import "dayjs/locale/en-gb";
import { AdditionalServiceType, Availability, AvailabilityResponse, CreateTemporaryBookingResponse } from "generated/types/types";
import { http } from "api/HttpClient";
import { WorkDetails } from "types/NewBooking";
import { LoadingBar } from "components/LoadingBar";
import { ModalCentered } from "components/generic/ModalCentered";
import { ClearTemporaryBooking } from "views/NewBooking/ClearTemporaryBooking";
import BeatLoader from "react-spinners/BeatLoader";
import { useTranslation } from "react-i18next";

registerLocale("en", enGB);
registerLocale("da", da);

const localeMap: Record<string, string> = {
  en: "en",
  dk: "da",
};

const dayjsLocaleMap: Record<string, string> = {
  en: "en-gb",
  dk: "da",
};
dayjs.extend(advancedFormat);

interface Props extends INewBookingPropsBase {
    currentSelectedBookingDates: Date[];
    setSelectedDates: (value: Date[]) => void;
    micrositePin: string;
    vehicleRegNo: string;
    additionalServiceRequested: AdditionalServiceType;
    workDetails: WorkDetails | null;
    encryptedSiteId: string;
    calendarDaysBlockOutCollectionAndDelivery: number;
    calendarDaysBlockOutCourtesyCar: number;
    calendarDaysBlockOutDropOff: number;
    calendarDaysBlockOutWhileYouWait: number;
    minimumNumberOfDiarySlotsAllowed: number;
    isDiaryIntegrated: boolean;
    setDiaryIntegrationFlag: (isDiaryIntegated: boolean) => void;
    goToPreviousStep: () => void;
    goToWorkRequired: () => void;
    referenceCode: string;
    setTempBookingReference: (referenceCode: string) => void;
    tempBookingReference: string;
    odometerReading: string | null;
}

export const ChooseDateAndTime: React.FC<Props> = (props: Props) => {
    const [calendarSelectedDate, setCalendarSelectedDate] = useState<Date | null>();
    const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
    const [invalidDateSelected, setInvalidDateSelected] = useState<boolean>(false);
    const [invalidTimeSelected, setInvalidTimeSelected] = useState<boolean>(false);
    const [includedTimes, setIncludedTimes] = useState<Date[] | null>(null);
    const [newDateAdded, setNewDateAdded] = useState<boolean>(false);
    const [serviceCentreLeadTime, setServiceCentreLeadTime] = useState(0);
    const [availability, setAvailability] = useState<Availability[]>([]);
    const [includeDates, setIncludedDates] = useState<Date[]>([]);
    const [selectedDateTime, setSelectedDateTime] = useState<Date[]>(props.currentSelectedBookingDates);
    const [isDiaryIntegrated, setIsDiaryIntegrated] = useState(props.isDiaryIntegrated);
    const initialRender = useRef(true);
    const initialRenderForNoDatesPopup = useRef(true);
    const [isLoading, setIsLoading] = useState(false);
    const [showNoDatesPopup, setShowNoDatesPopup] = useState(false);
    const [showNoTimeSlotPopup, setShowNoTimeSlotPopup] = useState(false);
    const [refreshCalendar, setRefreshCalendar] = useState(1);
    const [modalContainer, setModalContainer] = useState<React.ReactNode>(null);
    const [isTempBookingApiLoading, setIsTempBookingApiLoading] = useState(false);
    //
    const minimumNumberOfSlotAllowed = isDiaryIntegrated ? 1 : props.minimumNumberOfDiarySlotsAllowed;
    const maximumNumberOfSlotsAllowed = isDiaryIntegrated ? 1 : 3;
    const colDelPostalCode = (props.workDetails?.collectionAddress || "").split("\n").pop()?.substring(0, 3) || "";
    //

    const { i18n, t } = useTranslation();
    useEffect(() => {
        dayjs.locale(dayjsLocaleMap[i18n.language] || "en-gb");
    }, [i18n.language]);

    const blockOutDays = useMemo<Date[]>(() => {
        if (isDiaryIntegrated) {
            return [];
        }
        let blockOutDays: number;
        const calendarDaysBlockOutOperation: Date[] = [];

        switch (props.additionalServiceRequested) {
            case AdditionalServiceType.Delivery:
                blockOutDays = props.calendarDaysBlockOutCollectionAndDelivery
                break;
            case AdditionalServiceType.Courtesy:
                blockOutDays = props.calendarDaysBlockOutCourtesyCar
                break;
            case AdditionalServiceType.WhileUWait:
                blockOutDays = props.calendarDaysBlockOutWhileYouWait
                break;
            case AdditionalServiceType.NoAdditional:
                blockOutDays = props.calendarDaysBlockOutDropOff
                break;
            default:
                blockOutDays = 0
                break;
        }

        if (includeDates.length) {
            const firstDay = dayjs(includeDates[0]);
            for (let i = 0; i < blockOutDays; i++) {
                calendarDaysBlockOutOperation.push(firstDay.add(i, "day").toDate());
            }
        }

        return calendarDaysBlockOutOperation;
    }, [
        includeDates,
        props.additionalServiceRequested,
        props.calendarDaysBlockOutCollectionAndDelivery,
        props.calendarDaysBlockOutCourtesyCar,
        props.calendarDaysBlockOutDropOff,
        props.calendarDaysBlockOutWhileYouWait,
        isDiaryIntegrated
    ]);
    const leadTime = useMemo<Date[]>(() => {
        if (isDiaryIntegrated) {
            return [];
        }
        const leadTimeDays: Date[] = [];
        if (includeDates.length) {
            const firstDay = dayjs(includeDates[0]);
            for (let i = 0; i < serviceCentreLeadTime; i++) {
                leadTimeDays.push(firstDay.add(i, "day").toDate());
            }
        }
        return leadTimeDays;
    }, [includeDates, isDiaryIntegrated, serviceCentreLeadTime]);
    const headingDescription = useMemo(() => {
        if (isDiaryIntegrated) {
            return <>
                <p>{t("date_picker_warning_message")}</p>
                <p>{t("date_picker_warning_message_info")}</p>
            </>
        }
        switch (minimumNumberOfSlotAllowed) {
            case 1:
                return <>
                    <p>{t("date_picker_select_3_dates_and_times")}</p>
                    <p>{t("date_picker_select_dates_and_times")}</p>
                </>;
            case 2:
                return <>
                    <p>{t("date_picker_select_dates_and_times_2")}</p>
                    <p>{t("date_picker_select_dates_and_times")}</p>
                </>;
            case 3:
                return <>
                    <p>{t("date_picker_select_dates_and_times_3")}</p>
                    <p>{t("date_picker_select_dates_and_times")}</p>
                </>;
            default:
                return null;
        }
    }, [minimumNumberOfSlotAllowed, isDiaryIntegrated]);
    //
    const getAvailableTimes = useCallback((date: typeof calendarSelectedDate) => {
        if (!date) return [];

        const selectedDate = dayjs(date).startOf("day");
        const matchedDate = availability.find(({ availableDate }) => dayjs(availableDate).isSame(selectedDate));
        const times: Date[] = [];

        (matchedDate?.availableTimes || [])
            .filter(t => {
                if (props.additionalServiceRequested === AdditionalServiceType.WhileUWait) {
                    return t.whileYouWait;
                }
                return true;
            })
            .forEach(t => {
                const [h, m] = (() => {
                    const time = String(t.time).split(".");
                    return [Number(time[0]) || 0, Number((time[1] || "").padEnd(2, "0")) || 0];
                })();
                times.push(selectedDate.hour(h).minute(m).toDate());
            });

        return times;
    }, [availability, props.additionalServiceRequested]);
    //
    const showGetAvailabilityAlert = useCallback((messageBody: string) => {
        setModalContainer(
            <ModalCentered size="sm" show={true} onHide={() => { }} contentClassName="custom-dialog p-4">
                <Modal.Body>
                    <h2 className="mb-3">{messageBody}</h2>
                    <Button size="lg" onClick={() => props.goToPreviousStep()}>
                        {t("date_picker_continue")}
                    </Button>
                </Modal.Body>
            </ModalCentered>
        );
    }, [props]);

    useEffect(() => scrollToTop(), []);

    useEffect(() => {

        const request: { [key: string]: unknown } = {
            encryptedSiteId: props.encryptedSiteId,
            micrositePin: props.micrositePin,
            isRequestDiaryIntegrated: props.isDiaryIntegrated,
            vehicleReg: props.vehicleRegNo,
            collectionDeliveryRequested: props.additionalServiceRequested === AdditionalServiceType.Delivery,
            courtesyCarRequested: props.additionalServiceRequested === AdditionalServiceType.Courtesy,
            WhileYouWaitRequested: props.additionalServiceRequested === AdditionalServiceType.WhileUWait,
            DropOffRequested: props.additionalServiceRequested === AdditionalServiceType.NoAdditional,
            workRequired: props.workDetails?.workRequired || [],
            repairOtherText: props.workDetails?.repairOtherText || "",
            additionalNotes: props.workDetails?.additionalNotes || "",
            referenceCode: props.referenceCode,
            collectionPostCode: props.workDetails?.collectionPostCode || ""
        };

        setIsLoading(true);
        http.post<AvailabilityResponse>("/ServiceCentre/GetAvailability", request).then(({ data }) => {
            if (data.errorResponse && data.errorResponse.errorCode === 100) {
                showGetAvailabilityAlert(data.errorResponse.errorDetails);
                return;
            }
            if (data.result) {
                data.result = data.result.map(a => ({ ...a, availableDate: dayjs(a.availableDate).startOf("d").toDate() }));
                ReactDOM.unstable_batchedUpdates(() => {
                    setAvailability(data.result);
                    setIsDiaryIntegrated(data.isFromDiaryIntegration);
                    setServiceCentreLeadTime(data.leadTime);
                });
            }
        }).finally(() => {
            setIsLoading(false);
        });
        /* Disabling eslint warning to avoid multiple getAvailability call */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.additionalServiceRequested, props.encryptedSiteId, props.isDiaryIntegrated, props.micrositePin, props.vehicleRegNo, props.workDetails?.additionalNotes, props.workDetails?.repairOtherText, props.workDetails?.workRequired, refreshCalendar]);

    useEffect(() => {
        if (initialRenderForNoDatesPopup.current) { initialRenderForNoDatesPopup.current = false; return; }
        const filteredAvailableDates = availability.filter(a => {
            if (!isDiaryIntegrated) {
                return true;
            }
            if (props.additionalServiceRequested === AdditionalServiceType.Courtesy) {
                return a.courtesyVehicle;
            }
            if (props.additionalServiceRequested === AdditionalServiceType.Delivery) {
                const colDelPostalCodeAreas = a.colDelPostcodeAreas || [];

                /* For all DI providers which are not returning C&D postalCodes in their API response and sending [""] for proceeding date selection */
                if (colDelPostalCodeAreas.length === 1 && colDelPostalCodeAreas[0] === "") return true;

                return (colDelPostalCodeAreas || []).includes(colDelPostalCode);
            }
            if (props.additionalServiceRequested === AdditionalServiceType.WhileUWait) {
                return a.availableTimes.filter(t => t.whileYouWait).length > 0
            }
            return true;
        }).map(date => date.availableDate);
        setIncludedDates(filteredAvailableDates);
        if (isDiaryIntegrated && filteredAvailableDates.length === 0) {
            setShowNoDatesPopup(true);
            setSelectedDateTime([]);
        }
    }, [availability, colDelPostalCode, props.additionalServiceRequested, isDiaryIntegrated]);

    useEffect(() => {
        setIncludedTimes(getAvailableTimes(calendarSelectedDate));

    }, [calendarSelectedDate, getAvailableTimes]);

    useEffect(() => {

        const availabilityDates = includeDates.filter(a => {
            return [...blockOutDays, ...leadTime].findIndex(excludedDate => dayjs(excludedDate).isSame(a)) < 0
        });

        if (availabilityDates.length === 0) {
            setCalendarSelectedDate(null);
            return;
        }

        if (initialRender.current && !(props.isDiaryIntegrated && props.tempBookingReference)) {
            const availabilityWithoutLeadTime = includeDates.filter(a => {
                return blockOutDays.findIndex(excludedDate => dayjs(excludedDate).isSame(a)) < 0
            });
            const availableSelectedDateTime = selectedDateTime.filter(selectedDate => {
                return availabilityWithoutLeadTime.findIndex(a => dayjs(selectedDate).startOf("d").isSame(a)) > -1 &&
                    getAvailableTimes(selectedDate).findIndex(dt => dayjs(selectedDate).isSame(dt)) > -1;
            }).slice(0, maximumNumberOfSlotsAllowed);

            initialRender.current = false;
            if (availableSelectedDateTime.length !== selectedDateTime.length) {
                setSelectedDateTime(availableSelectedDateTime);
                return;
            }
        }

        if (selectedDateTime.length === 0) {
            let availableDateIndex = 0;
            if (isDiaryIntegrated && dayjs(availabilityDates[0]).startOf("d").isSame(dayjs().startOf("d"))) {
                availableDateIndex = 1;
            }
            setCalendarSelectedDate(getAvailableTimes(availabilityDates[availableDateIndex])[0] || availabilityDates[availableDateIndex]);
            return;
        }

        if (selectedDateTime.length === maximumNumberOfSlotsAllowed) {
            setCalendarSelectedDate(null);
            return;
        }

        const lastSelectedDate = dayjs(selectedDateTime[selectedDateTime.length - 1]).startOf("d");

        if (leadTime.findIndex(l => lastSelectedDate.isSame(l)) > -1) {
            setCalendarSelectedDate(availabilityDates[0]);
            return;
        }

        const lastDateIndex = availabilityDates.findIndex(a => dayjs(a).isSame(lastSelectedDate))

        if (lastDateIndex > -1) {
            setCalendarSelectedDate((availabilityDates[lastDateIndex + 1]) || null);
            return;
        }

        setCalendarSelectedDate(null);

    }, [includeDates, blockOutDays, selectedDateTime, leadTime, maximumNumberOfSlotsAllowed, isDiaryIntegrated, getAvailableTimes]);

    // This will scroll to the currently selected time which changes when adding a new date back to the default of 9am
    useEffect(() => {
        if (newDateAdded) {
            const selectedTimeElement = document.getElementsByClassName("react-datepicker__time-list-item--selected")[0];
            if (selectedTimeElement) {
                selectedTimeElement.scrollIntoView({ block: "nearest" });
            }
        }
    }, [newDateAdded]);

    const handleAddButtonClick = () => {

        if (!calendarSelectedDate || selectedDateTime.length >= maximumNumberOfSlotsAllowed) {
            return;
        }

        if (calendarSelectedDate <= new Date()) {
            setInvalidDateSelected(true);
            return;
        }

        if ((includedTimes || []).filter(t => t.getTime() === calendarSelectedDate.getTime()).length === 0) {
            setInvalidTimeSelected(true);
            return;
        }

        setSelectedDateTime(() => {
            const dt = [...selectedDateTime, calendarSelectedDate];
            dt.sort((date1, date2) => date1.getTime() - date2.getTime());
            return dt;
        });

        setInvalidDateSelected(false);
        setInvalidTimeSelected(false);
        setNewDateAdded(true);
    }

    const removeDate = (index: number) => {
        if (props.isDiaryIntegrated && props.tempBookingReference) {
            showClearTemporaryBookingConfirmation();
        }
        else {
            setSelectedDateTime(selectedDateTime.filter((x, i) => i !== index));
            setNewDateAdded(false);
        }
    }

    const selectedDatesList = () => {
        if (selectedDateTime.length <= 0) {
            return (
                <div className="row selected-date">
                    <div className="col-12 vert-align-col selected-dates-placeholder">
                        <p>{t("date_picker_no_dates_selected")}</p>
                    </div>
                </div>
            );
        } else {
            return selectedDateTime.map((item, index) => selectedDatePart(`${dayjs(item).format("ddd Do MMM YYYY")} ${t("date_picker_at")} ${dayjs(item).format("HH:mm")}`, index));
        }
    };

    const selectedDatePart = (date: string, index: number) => {
        return (
            <div className="row selected-date" key={index}>
                <div className="col-10 vert-align-col">
                    <p>{date}</p>
                </div>
                <div className="col-2 vert-align-col">
                    <button type="button" className="btn btn-light remove-item-button" onClick={() => removeDate(index)}>
                        <CrossIcon />
                    </button>
                </div>
            </div>
        );
    };
    const customDayElement = (day: number) => {
        return <div className="day-element">{day}</div>;
    };

    const getDatePickerComponent = () => {
        return (
            <DatePicker
                key={i18n.language}
                selected={calendarSelectedDate}
                onChange={date => {
                    if (date && date instanceof Date) {
                        setCalendarSelectedDate(date);
                    }
                }}
                excludeDates={[...blockOutDays, ...selectedDateTime]}
                includeDates={includeDates}
                showTimeSelect={true}
                timeCaption={t("date_picker_time")}
                locale={localeMap[i18n.language] || "en"}
                disabled
                inline
                timeIntervals={1}
                includeTimes={includedTimes !== null ? includedTimes : []}
                dayClassName={calendarControlDate => {
                    const calendarDate = dayjs(calendarControlDate).startOf("day");
                    if (blockOutDays.findIndex(b => calendarDate.isSame(b)) > -1) {
                        return "closed-day";
                    }
                    if (leadTime.findIndex(l => calendarDate.isSame(l)) > -1) {
                        return "lead-time";
                    }
                    return null;
                }}
                renderDayContents={customDayElement}
                renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                    <div className="row no-gutters">
                        <div className="col-2">
                            <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                <LeftArrowIcon />
                            </button>
                        </div>
                        <div className="col-8">
                            <h5 className="dp-month">{dayjs(date).format("MMMM")}</h5>
                        </div>
                        <div className="col-2">
                            <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                                <RightArrowIcon />
                            </button>
                        </div>
                    </div>
                )}
            />
        );
    };

    const handleSubmit = () => {
        if (isTempBookingApiLoading) return;
        setSubmitAttempted(true);
        if (selectedDateTime.length >= minimumNumberOfSlotAllowed) {

            if (isDiaryIntegrated && !props.tempBookingReference) {

                const request: { [key: string]: unknown } = {
                    EncryptedSiteId: props.encryptedSiteId,
                    MicrositePin: props.micrositePin,
                    VehicleReg: props.vehicleRegNo,
                    PreferedDateTime: dayjs(selectedDateTime[0]).format("YYYY-MM-DDTHH:mm:ss") || "",
                    CollectionDeliveryRequested: props.additionalServiceRequested === AdditionalServiceType.Delivery,
                    OdoReading: props.odometerReading,
                    WorkRequired: props.workDetails?.workRequired || [],
                    ReferenceCode: props.referenceCode
                };

                setIsTempBookingApiLoading(true);

                http.post<CreateTemporaryBookingResponse>("/Booking/CreateTemporaryBooking", request).then(({ data }) => {
                    props.setTempBookingReference(data.temporaryBookingReference || "");
                    if (data.errorResponse && data.errorResponse.errorCode === 100) {
                        setShowNoTimeSlotPopup(true);
                        return "ERROR";
                    }
                    if (data.errorResponse === null) {
                        props.setDiaryIntegrationFlag(true);
                    }
                }).catch(() => {
                    props.setDiaryIntegrationFlag(false);
                }).then(data => {
                    setIsTempBookingApiLoading(false);
                    if (data === "ERROR") return;
                    props.setSelectedDates(selectedDateTime);
                    props.goToNextStep();
                });

            } else {
                props.setDiaryIntegrationFlag(isDiaryIntegrated);
                props.goToNextStep();
                props.setSelectedDates(selectedDateTime);
            }
        }
    };

    const getNoDatesWorkTypeMessage = () => {
        switch (props.additionalServiceRequested) {
            case AdditionalServiceType.Courtesy:
                return t("date_picker_courtesy_car");
            case AdditionalServiceType.Delivery:
                return t("date_picker_collection");
            case AdditionalServiceType.WhileUWait:
                return t("date_picker_whileuwait");
            case AdditionalServiceType.NoAdditional:
                return t("date_picker_drop_off");
            default:
                return "";
        }
    }

    const workType = getNoDatesWorkTypeMessage();

    const onContinueNoTimeSlotPopup = () => {
        setSelectedDateTime([]);
        setRefreshCalendar(previousValue => previousValue + 1);
        setShowNoTimeSlotPopup(false);
    }

    const showClearTemporaryBookingConfirmation = () => {
        setModalContainer(
            <ClearTemporaryBooking apiRequest={{
                EncryptedSiteId: props.encryptedSiteId,
                MicrositePin: props.micrositePin,
                VehicleReg: props.vehicleRegNo,
                TemporaryBookingReference: props.tempBookingReference
            }} onSuccess={() => {
                props.setTempBookingReference("");
                setSelectedDateTime([]);
                props.setSelectedDates([]);
                setNewDateAdded(false);
                setModalContainer(null);
                setRefreshCalendar(previousValue => previousValue + 1);
            }} onCancel={() => setModalContainer(null)} />
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <Hero heading={t("date_picker_time_date")}>{headingDescription}</Hero>
                </div>
            </div>
            <div className="row datetime-selector">
                <div className="col-12">
                    <div className="row">
                        <div className="selected-date--list col-xl-5">
                            <div className="row">
                                <h2>{t("date_picker_dates_selected")}</h2>
                            </div>
                            {selectedDatesList()}
                        </div>
                        <div className="order-first order-xl-last col-xl-7 date-picker-container">
                            <div className="lead-time-warning">{t("date_picker_dates_highlighted")}</div>
                            <div className="date-picker-inner-container">{isLoading ? <LoadingBar /> : getDatePickerComponent()}</div>
                            <Button onClick={handleAddButtonClick} disabled={selectedDateTime.length >= maximumNumberOfSlotsAllowed}>
                                {selectedDateTime.length >= maximumNumberOfSlotsAllowed ? t("date_picker_dates_selected_max") : t("date_picker_add_date")}
                            </Button>
                            <div className="col-12 date-picker-validation-error">
                                {invalidDateSelected ? (
                                    <div className="error-msg">{t("date_picker_date_future")}</div>
                                ) : invalidTimeSelected ? (
                                    <div className="error-msg">{t("date_picker_time_range")}</div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-12 date-picker-validation-error">
                    {
                        selectedDateTime.length < minimumNumberOfSlotAllowed && submitAttempted &&
                        <div className="error-msg">
                            {t("date_picker_minimum_slots_message", { count: minimumNumberOfSlotAllowed })}
                        </div>
                    }
                </div>
                <div className="col-12">
                    <Button className="large-input" onClick={handleSubmit}>
                        {isTempBookingApiLoading ? <BeatLoader color={"white"} loading={true} /> : t("date_picker_continue")}
                    </Button>
                </div>
            </div>
            <ModalCentered size="lg" show={showNoDatesPopup} contentClassName="custom-dialog" onHide={() => { setShowNoDatesPopup(false) }} backdrop="static">
                <Modal.Body>
                    <h1>{t("date_picker_no_dates")}</h1>
                    <h2>
                        {t("date_picker_no_dates_for_option", { workType: workType})}
                    </h2>
                    <Button size="lg" onClick={props.goToWorkRequired}>{t("date_picker_change_booking")}</Button>
                    <Button size="lg" onClick={props.goToPreviousStep}>{t("date_picker_new_garage_search")}</Button>
                </Modal.Body>
            </ModalCentered>
            <ModalCentered size="sm" show={showNoTimeSlotPopup} onHide={() => { }} contentClassName="custom-dialog p-4">
                <Modal.Body>
                    <h1>{t("date_picker_timeslot_not_available")}</h1>
                    <h2 className="mb-3">{t("date_picker_timeslot_message")}</h2>
                    <Button size="lg" onClick={() => onContinueNoTimeSlotPopup()}>
                        {t("date_picker_continue")}
                    </Button>
                </Modal.Body>
            </ModalCentered>
            {modalContainer}
        </>
    );
};